/**
 * Validates that organization ID is present.
 * Throws an error if missing.
 *
 * @param orgId - Organization ID from context
 * @throws Error if orgId is missing
 */
export function validateOrgId(orgId: string | null | undefined): asserts orgId is string {
  if (!orgId) {
    throw new Error('Organization ID is required')
  }
}

/**
 * Validates that a vessel ID is present.
 * Throws an error if the vessel ID is missing.
 *
 * @param vesselId - Vessel ID to validate
 * @throws Error if vessel ID is missing
 */
export function validateVesselId(vesselId: string | null | undefined): asserts vesselId is string {
  if (!vesselId) {
    throw new Error('Vessel ID is required')
  }
}

/**
 * Validates that a connected account ID is present.
 * Throws an error if the connected account ID is missing.
 *
 * @param connectedAccountId - Connected account ID to validate
 * @throws Error if connected account ID is missing
 */
export function validateConnectedAccountId(
  connectedAccountId: string | null | undefined,
): asserts connectedAccountId is string {
  if (!connectedAccountId) {
    throw new Error('Connected Account ID is required')
  }
}

/**
 * Validates that organization, vessel, and connected account IDs are present.
 * Use for vessel-scoped connected account API calls.
 *
 * @param orgId - Organization ID from context
 * @param vesselId - Vessel ID from context
 * @param connectedAccountId - Connected account ID from context
 * @returns Object with validated IDs
 * @throws Error if any ID is missing
 */
export function validateContextIds(
  orgId: string | null | undefined,
  vesselId: string | null | undefined,
  connectedAccountId: string | null | undefined,
): { orgId: string; vesselId: string; connectedAccountId: string } {
  validateOrgId(orgId)
  validateVesselId(vesselId)
  validateConnectedAccountId(connectedAccountId)

  return { orgId, vesselId, connectedAccountId }
}

/**
 * Validates that a card ID is present.
 * Throws an error if the card ID is missing.
 *
 * @param cardId - Card ID to validate
 * @throws Error if card ID is missing
 */
export function validateCardId(cardId: string | null | undefined): asserts cardId is string {
  if (!cardId) {
    throw new Error('Card ID is required')
  }
}

/**
 * Validates that a cardholder ID is present.
 * Throws an error if the cardholder ID is missing.
 *
 * @param cardholderId - Cardholder ID to validate
 * @throws Error if cardholder ID is missing
 */
export function validateCardholderId(cardholderId: string | null | undefined): asserts cardholderId is string {
  if (!cardholderId) {
    throw new Error('Cardholder ID is required')
  }
}

/**
 * Validates that a beneficiary ID is present.
 * Throws an error if the beneficiary ID is missing.
 *
 * @param beneficiaryId - Beneficiary ID to validate
 * @throws Error if beneficiary ID is missing
 */
export function validateBeneficiaryId(beneficiaryId: string | null | undefined): asserts beneficiaryId is string {
  if (!beneficiaryId) {
    throw new Error('Beneficiary ID is required')
  }
}

/**
 * Validates that a transaction ID is present.
 * Throws an error if the transaction ID is missing.
 *
 * @param transactionId - Transaction ID to validate
 * @throws Error if transaction ID is missing
 */
export function validateTransactionId(transactionId: string | null | undefined): asserts transactionId is string {
  if (!transactionId) {
    throw new Error('Transaction ID is required')
  }
}

/**
 * Validates that an attachment ID is present.
 * Throws an error if the attachment ID is missing.
 *
 * @param attachmentId - Attachment ID to validate
 * @throws Error if attachment ID is missing
 */
export function validateAttachmentId(attachmentId: string | null | undefined): asserts attachmentId is string {
  if (!attachmentId) {
    throw new Error('Attachment ID is required')
  }
}

/**
 * Validates that a charter ID is present.
 * Throws an error if the charter ID is missing.
 *
 * @param charterId - Charter ID to validate
 * @throws Error if charter ID is missing
 */
export function validateCharterId(charterId: string | null | undefined): asserts charterId is string {
  if (!charterId) {
    throw new Error('Charter ID is required')
  }
}

/**
 * Validates organization ID and a vessel ID.
 * Convenience function for hooks that require both.
 *
 * @param orgId - Organization ID from context
 * @param vesselId - Vessel ID to validate
 * @returns Object with validated IDs including vesselId
 * @throws Error if any required ID is missing
 */
export function validateVesselQueryParams(
  orgId: string | null | undefined,
  vesselId: string | null | undefined,
): { orgId: string; vesselId: string } {
  validateOrgId(orgId)
  validateVesselId(vesselId)
  return { orgId, vesselId }
}

/**
 * Validates that a user ID is present.
 * Throws an error if the user ID is missing.
 *
 * @param userId - User ID to validate
 * @throws Error if user ID is missing
 */
export function validateUserId(userId: string | null | undefined): asserts userId is string {
  if (!userId) {
    throw new Error('User ID is required')
  }
}

/**
 * Validates that an invite ID is present.
 * Throws an error if the invite ID is missing.
 *
 * @param inviteId - Invite ID to validate
 * @throws Error if invite ID is missing
 */
export function validateInviteId(inviteId: string | null | undefined): asserts inviteId is string {
  if (!inviteId) {
    throw new Error('Invite ID is required')
  }
}

/**
 * Validates that a Xero tenant ID is present.
 * Throws an error if the tenant ID is missing.
 *
 * @param xeroTenantId - Xero tenant ID to validate
 * @throws Error if xeroTenantId is missing
 */
export function validateXeroTenantId(xeroTenantId: string | null | undefined): asserts xeroTenantId is string {
  if (!xeroTenantId) {
    throw new Error('xeroTenantId is required')
  }
}

/**
 * Validates context IDs and a card ID.
 * Convenience function for hooks that require both.
 *
 * @param orgId - Organization ID from context
 * @param vesselId - Vessel ID from context
 * @param connectedAccountId - Connected account ID from context
 * @param cardId - Card ID to validate
 * @returns Object with validated IDs including cardId
 * @throws Error if any required ID is missing
 */
export function validateCardQueryParams(
  orgId: string | null | undefined,
  vesselId: string | null | undefined,
  connectedAccountId: string | null | undefined,
  cardId: string | null | undefined,
): { orgId: string; vesselId: string; connectedAccountId: string; cardId: string } {
  const {
    orgId: validatedOrgId,
    vesselId: validatedVesselId,
    connectedAccountId: validatedConnectedAccountId,
  } = validateContextIds(orgId, vesselId, connectedAccountId)

  validateCardId(cardId)
  return { orgId: validatedOrgId, vesselId: validatedVesselId, connectedAccountId: validatedConnectedAccountId, cardId }
}

/**
 * Validates organization, vessel, connected account and card IDs for vessel-scoped card API calls.
 *
 * @param orgId - Organization ID from context
 * @param vesselId - Vessel ID from context
 * @param connectedAccountId - Connected account ID from context
 * @param cardId - Card ID to validate
 * @returns Object with validated IDs
 * @throws Error if any required ID is missing
 */
export function validateVesselCardQueryParams(
  orgId: string | null | undefined,
  vesselId: string | null | undefined,
  connectedAccountId: string | null | undefined,
  cardId: string | null | undefined,
): { orgId: string; vesselId: string; connectedAccountId: string; cardId: string } {
  const {
    orgId: validatedOrgId,
    vesselId: validatedVesselId,
    connectedAccountId: validatedConnectedAccountId,
  } = validateContextIds(orgId, vesselId, connectedAccountId)

  validateCardId(cardId)
  return { orgId: validatedOrgId, vesselId: validatedVesselId, connectedAccountId: validatedConnectedAccountId, cardId }
}

/**
 * Validates context IDs and a cardholder ID.
 * Convenience function for hooks that require both.
 *
 * @param orgId - Organization ID from context
 * @param vesselId - Vessel ID from context
 * @param connectedAccountId - Connected account ID from context
 * @param cardholderId - Cardholder ID to validate
 * @returns Object with validated IDs including cardholderId
 * @throws Error if any required ID is missing
 */
export function validateCardholderQueryParams(
  orgId: string | null | undefined,
  vesselId: string | null | undefined,
  connectedAccountId: string | null | undefined,
  cardholderId: string | null | undefined,
): { orgId: string; vesselId: string; connectedAccountId: string; cardholderId: string } {
  const {
    orgId: validatedOrgId,
    vesselId: validatedVesselId,
    connectedAccountId: validatedConnectedAccountId,
  } = validateContextIds(orgId, vesselId, connectedAccountId)

  validateCardholderId(cardholderId)
  return {
    orgId: validatedOrgId,
    vesselId: validatedVesselId,
    connectedAccountId: validatedConnectedAccountId,
    cardholderId,
  }
}

/**
 * Validates organization, vessel, connected account and cardholder IDs for vessel-scoped cardholder API calls.
 */
export function validateVesselCardholderQueryParams(
  orgId: string | null | undefined,
  vesselId: string | null | undefined,
  connectedAccountId: string | null | undefined,
  cardholderId: string | null | undefined,
): { orgId: string; vesselId: string; connectedAccountId: string; cardholderId: string } {
  const {
    orgId: validatedOrgId,
    vesselId: validatedVesselId,
    connectedAccountId: validatedConnectedAccountId,
  } = validateContextIds(orgId, vesselId, connectedAccountId)

  validateCardholderId(cardholderId)
  return {
    orgId: validatedOrgId,
    vesselId: validatedVesselId,
    connectedAccountId: validatedConnectedAccountId,
    cardholderId,
  }
}

/**
 * Validates context IDs and a beneficiary ID.
 * Convenience function for hooks that require both.
 *
 * @param orgId - Organization ID from context
 * @param vesselId - Vessel ID from context
 * @param connectedAccountId - Connected account ID from context
 * @param beneficiaryId - Beneficiary ID to validate
 * @returns Object with validated IDs including beneficiaryId
 * @throws Error if any required ID is missing
 */
export function validateBeneficiaryQueryParams(
  orgId: string | null | undefined,
  vesselId: string | null | undefined,
  connectedAccountId: string | null | undefined,
  beneficiaryId: string | null | undefined,
): { orgId: string; vesselId: string; connectedAccountId: string; beneficiaryId: string } {
  const {
    orgId: validatedOrgId,
    vesselId: validatedVesselId,
    connectedAccountId: validatedConnectedAccountId,
  } = validateContextIds(orgId, vesselId, connectedAccountId)

  validateBeneficiaryId(beneficiaryId)
  return {
    orgId: validatedOrgId,
    vesselId: validatedVesselId,
    connectedAccountId: validatedConnectedAccountId,
    beneficiaryId,
  }
}

/**
 * Validates organization, vessel, connected account and beneficiary IDs for vessel-scoped beneficiary API calls.
 *
 * @param orgId - Organization ID from context
 * @param vesselId - Vessel ID from context
 * @param connectedAccountId - Connected account ID from context
 * @param beneficiaryId - Beneficiary ID to validate
 * @returns Object with validated IDs including beneficiaryId
 * @throws Error if any required ID is missing
 */
export function validateVesselBeneficiaryQueryParams(
  orgId: string | null | undefined,
  vesselId: string | null | undefined,
  connectedAccountId: string | null | undefined,
  beneficiaryId: string | null | undefined,
): { orgId: string; vesselId: string; connectedAccountId: string; beneficiaryId: string } {
  const {
    orgId: validatedOrgId,
    vesselId: validatedVesselId,
    connectedAccountId: validatedConnectedAccountId,
  } = validateContextIds(orgId, vesselId, connectedAccountId)

  validateBeneficiaryId(beneficiaryId)
  return {
    orgId: validatedOrgId,
    vesselId: validatedVesselId,
    connectedAccountId: validatedConnectedAccountId,
    beneficiaryId,
  }
}

/**
 * Validates context IDs and a transaction ID.
 * Convenience function for hooks that require both.
 *
 * @param orgId - Organization ID from context
 * @param vesselId - Vessel ID from context
 * @param connectedAccountId - Connected account ID from context
 * @param transactionId - Transaction ID to validate
 * @returns Object with validated IDs including transactionId
 * @throws Error if any required ID is missing
 */
export function validateTransactionQueryParams(
  orgId: string | null | undefined,
  vesselId: string | null | undefined,
  connectedAccountId: string | null | undefined,
  transactionId: string | null | undefined,
): { orgId: string; vesselId: string; connectedAccountId: string; transactionId: string } {
  const {
    orgId: validatedOrgId,
    vesselId: validatedVesselId,
    connectedAccountId: validatedConnectedAccountId,
  } = validateContextIds(orgId, vesselId, connectedAccountId)

  validateTransactionId(transactionId)
  return {
    orgId: validatedOrgId,
    vesselId: validatedVesselId,
    connectedAccountId: validatedConnectedAccountId,
    transactionId,
  }
}

/**
 * Validates context IDs, transaction ID, and attachment ID.
 * Convenience function for hooks that require all three.
 *
 * @param orgId - Organization ID from context
 * @param vesselId - Vessel ID from context
 * @param connectedAccountId - Connected account ID from context
 * @param transactionId - Transaction ID to validate
 * @param attachmentId - Attachment ID to validate
 * @returns Object with validated IDs including transactionId and attachmentId
 * @throws Error if any required ID is missing
 */
export function validateTransactionAttachmentQueryParams(
  orgId: string | null | undefined,
  vesselId: string | null | undefined,
  connectedAccountId: string | null | undefined,
  transactionId: string | null | undefined,
  attachmentId: string | null | undefined,
): { orgId: string; vesselId: string; connectedAccountId: string; transactionId: string; attachmentId: string } {
  const {
    orgId: validatedOrgId,
    vesselId: validatedVesselId,
    connectedAccountId: validatedConnectedAccountId,
    transactionId: validatedTransactionId,
  } = validateTransactionQueryParams(orgId, vesselId, connectedAccountId, transactionId)

  validateAttachmentId(attachmentId)
  return {
    orgId: validatedOrgId,
    vesselId: validatedVesselId,
    connectedAccountId: validatedConnectedAccountId,
    transactionId: validatedTransactionId,
    attachmentId,
  }
}

/**
 * Validates context IDs and a charter ID.
 * Convenience function for hooks that require both.
 *
 * @param orgId - Organization ID from context
 * @param vesselId - Vessel ID from context
 * @param connectedAccountId - Connected account ID from context
 * @param charterId - Charter ID to validate
 * @returns Object with validated IDs including charterId
 * @throws Error if any required ID is missing
 */
export function validateCharterQueryParams(
  orgId: string | null | undefined,
  vesselId: string | null | undefined,
  connectedAccountId: string | null | undefined,
  charterId: string | null | undefined,
): { orgId: string; vesselId: string; connectedAccountId: string; charterId: string } {
  const {
    orgId: validatedOrgId,
    vesselId: validatedVesselId,
    connectedAccountId: validatedConnectedAccountId,
  } = validateContextIds(orgId, vesselId, connectedAccountId)

  validateCharterId(charterId)
  return {
    orgId: validatedOrgId,
    vesselId: validatedVesselId,
    connectedAccountId: validatedConnectedAccountId,
    charterId,
  }
}

/**
 * Validates context IDs, charter ID, and attachment ID.
 * Convenience function for hooks that require all three.
 *
 * @param orgId - Organization ID from context
 * @param vesselId - Vessel ID from context
 * @param connectedAccountId - Connected account ID from context
 * @param charterId - Charter ID to validate
 * @param attachmentId - Attachment ID to validate
 * @returns Object with validated IDs including charterId and attachmentId
 * @throws Error if any required ID is missing
 */
export function validateCharterAttachmentQueryParams(
  orgId: string | null | undefined,
  vesselId: string | null | undefined,
  connectedAccountId: string | null | undefined,
  charterId: string | null | undefined,
  attachmentId: string | null | undefined,
): { orgId: string; vesselId: string; connectedAccountId: string; charterId: string; attachmentId: string } {
  const {
    orgId: validatedOrgId,
    vesselId: validatedVesselId,
    connectedAccountId: validatedConnectedAccountId,
    charterId: validatedCharterId,
  } = validateCharterQueryParams(orgId, vesselId, connectedAccountId, charterId)

  validateAttachmentId(attachmentId)
  return {
    orgId: validatedOrgId,
    vesselId: validatedVesselId,
    connectedAccountId: validatedConnectedAccountId,
    charterId: validatedCharterId,
    attachmentId,
  }
}

/**
 * Validates organization ID and a user ID.
 * Convenience function for hooks that require both.
 *
 * @param orgId - Organization ID from context
 * @param userId - User ID to validate
 * @returns Object with validated IDs including userId
 * @throws Error if any required ID is missing
 */
export function validateUserQueryParams(
  orgId: string | null | undefined,
  userId: string | null | undefined,
): { orgId: string; userId: string } {
  validateOrgId(orgId)
  validateUserId(userId)
  return { orgId, userId }
}

/**
 * Validates organization ID and an invite ID.
 * Convenience function for hooks that require both.
 *
 * @param orgId - Organization ID from context
 * @param inviteId - Invite ID to validate
 * @returns Object with validated IDs including inviteId
 * @throws Error if any required ID is missing
 */
export function validateInviteQueryParams(
  orgId: string | null | undefined,
  inviteId: string | null | undefined,
): { orgId: string; inviteId: string } {
  validateOrgId(orgId)
  validateInviteId(inviteId)
  return { orgId, inviteId }
}
